import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => {
        const refreshed = response.headers?.['x-refreshed-token'];
        if (refreshed && localStorage.getItem('token')) localStorage.setItem('token', refreshed);
        return response;
    },
    (error) => {
        const url = error.config?.url || '';
        const isAuthFlow = url.includes('/auth/login') || url.includes('/auth/mfa') || url.includes('/auth/change-password');
        if (error.response?.status === 401 && !isAuthFlow) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Notifications API
export const notificationsAPI = {
    list: () => api.get('/notifications'),
    markRead: (id) => api.post(`/notifications/${id}/read`),
    markAllRead: () => api.post('/notifications/read-all'),
};

// Push notifications API
export const pushAPI = {
    vapidKey: () => api.get('/push/vapid-public-key'),
    subscribe: (subscription) => api.post('/push/subscribe', subscription),
    unsubscribe: (endpoint) => api.post('/push/unsubscribe', { endpoint }),
    test: () => api.post('/push/test'),
};

// Auth API
export const authAPI = {
    login: (email, password, deviceToken) =>
        api.post('/auth/login', { email, password, device_token: deviceToken || undefined }),
    mfaVerify: (pendingToken, code, rememberDevice, deviceToken) =>
        api.post('/auth/mfa/verify', {
            pending_token: pendingToken,
            code,
            remember_device: !!rememberDevice,
            device_token: deviceToken || undefined,
        }),
    mfaRecoverCode: (pendingToken, code) =>
        api.post('/auth/mfa/recover-code', { pending_token: pendingToken, code }),
    getMe: () => api.get('/auth/me'),
    changePassword: (oldPassword, newPassword) =>
        api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),
    requestRecovery: (email) =>
        api.post('/auth/recovery', { email, reset_base_url: window.location.origin }),
    verifyRecovery: (token) => api.post(`/auth/recovery/verify?token=${encodeURIComponent(token)}`),
    resetRecovery: (token, newPassword) =>
        api.post('/auth/recovery/reset', { token, new_password: newPassword }),
};

// Users API (Admin only)
export const usersAPI = {
    getAll: () => api.get('/users'),
    getSellers: () => api.get('/users/sellers'),
    create: (data) => api.post('/users', data),
    updateProfile: (userId, data) => api.put(`/users/${userId}`, data),
    updateRole: (userId, role) => api.put(`/users/${userId}/role`, { role }),
    setManager: (userId, managerId) => api.put(`/users/${userId}/manager`, { manager_id: managerId }),
    updateCountries: (userId, countries) => api.put(`/users/${userId}/countries`, countries),
    resetPassword: (userId) => api.put(`/users/${userId}/reset-password`),
    resetMfa: (userId) => api.put(`/users/${userId}/reset-mfa`),
    unlock: (userId) => api.put(`/users/${userId}/unlock`),
    regenerateRecoveryCodes: (userId) => api.put(`/users/${userId}/regenerate-recovery-codes`),
    forcePasswordChangeAll: () => api.post('/users/force-password-change-all'),
    delete: (userId, reassignTo) => api.delete(`/users/${userId}${reassignTo ? `?reassign_to=${reassignTo}` : ''}`),
};

// Backup API (Admin only)
export const backupAPI = {
    list: () => api.get('/admin/backups'),
    create: () => api.post('/admin/backups'),
    getSettings: () => api.get('/admin/backups/settings'),
    updateSettings: (frequency) => api.put('/admin/backups/settings', { frequency }),
    updateEmailSettings: (email_enabled, email_recipients) => api.put('/admin/backups/email-settings', { email_enabled, email_recipients }),
    download: (id) => api.get(`/admin/backups/${id}/download`, { responseType: 'blob' }),
    restore: (file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post('/admin/backups/restore', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
};

// Clients API
export const clientsAPI = {
    getAll: () => api.get('/clients'),
    getById: (id) => api.get(`/clients/${id}`),
    create: (data) => api.post('/clients', data),
    update: (id, data) => api.put(`/clients/${id}`, data),
    delete: (id) => api.delete(`/clients/${id}`),
    scanCard: (imageBase64, mimeType) => api.post('/clients/scan-card', { image_base64: imageBase64, mime_type: mimeType }),
    geocodeStatus: () => api.get('/admin/geocode/status'),
    geocodeRun: () => api.post('/admin/geocode/run'),
    assignCountries: (countries, assigned_to) => api.post('/clients/assign-countries', { countries, assigned_to }),
    importExcel: (file, assign_to, country_filter, update_assignments) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('assign_to', assign_to);
        if (country_filter) fd.append('country_filter', country_filter);
        if (update_assignments) fd.append('update_assignments', 'true');
        return api.post('/clients/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
};

// Opportunities API
export const opportunitiesAPI = {
    getAll: () => api.get('/opportunities'),
    getById: (id) => api.get(`/opportunities/${id}`),
    create: (data) => api.post('/opportunities', data),
    update: (id, data) => api.put(`/opportunities/${id}`, data),
    updateStage: (id, stage) => api.put(`/opportunities/${id}/stage?stage=${stage}`),
    delete: (id) => api.delete(`/opportunities/${id}`),
    downloadQuote: (id) => api.get(`/opportunities/${id}/quote.xlsx`, { responseType: 'blob' }),
    duplicate: (id) => api.post(`/opportunities/${id}/duplicate`),
    export: (params = {}) => {
        const s = new URLSearchParams();
        s.set('fmt', params.fmt || 'xlsx');
        s.set('mode', params.mode || 'offerte');
        if (params.q) s.set('q', params.q);
        if (params.seller && params.seller !== 'all') s.set('seller', params.seller);
        if (params.year && params.year !== 'all') s.set('year', params.year);
        return api.get(`/opportunities/export?${s.toString()}`, { responseType: 'blob' });
    },
};

// Commesse API
export const commesseAPI = {
    getAll: () => api.get('/commesse'),
    create: (data) => api.post('/commesse', data),
    update: (id, data) => api.put(`/commesse/${id}`, data),
    delete: (id) => api.delete(`/commesse/${id}`),
    nextNumber: (year) => api.get(`/commesse/next-number${year ? `?year=${year}` : ''}`),
    exportExcel: (params = {}) => {
        const s = new URLSearchParams();
        if (params.q) s.set('q', params.q);
        if (params.year && params.year !== 'all') s.set('year', params.year);
        if (params.month && params.month !== 'all') s.set('month', params.month);
        if (params.status && params.status !== 'all') s.set('status', params.status);
        if (params.seller && params.seller !== 'all') s.set('seller', params.seller);
        const qs = s.toString();
        return api.get(`/commesse/export.xlsx${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
    },
    getWeeklyRecipients: () => api.get('/admin/reports/commesse/weekly-recipients'),
    setWeeklyRecipients: (emails) => api.put('/admin/reports/commesse/weekly-recipients', { emails }),
    sendWeekly: () => api.post('/admin/reports/commesse/send-weekly'),
};

// Activities API
export const activitiesAPI = {
    getAll: () => api.get('/activities'),
    create: (data) => api.post('/activities', data),
    update: (id, data) => api.put(`/activities/${id}`, data),
    delete: (id) => api.delete(`/activities/${id}`),
};

// Opportunity documents API
export const documentsAPI = {
    list: (oppId) => api.get(`/opportunities/${oppId}/documents`),
    upload: (oppId, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post(`/opportunities/${oppId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    view: (docId) => api.get(`/documents/${docId}/download`, { responseType: 'blob' }),
    link: (docId) => api.post(`/documents/${docId}/link`),
    setCover: (docId) => api.post(`/documents/${docId}/cover`),
    delete: (docId) => api.delete(`/documents/${docId}`),
};

// Notes API
export const notesAPI = {
    getAll: (clientId, opportunityId) => {
        const params = new URLSearchParams();
        if (clientId) params.append('client_id', clientId);
        if (opportunityId) params.append('opportunity_id', opportunityId);
        return api.get(`/notes?${params.toString()}`);
    },
    create: (data) => api.post('/notes', data),
};

// Dashboard API
export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
    getSalesByUser: (period = 'year') => api.get('/dashboard/sales-by-user', { params: { period } }),
    getLeaderboard: () => api.get('/dashboard/leaderboard'),
};

// Relazioni di viaggio
export const relazioniAPI = {
    getAll: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.country) qs.append('country', params.country);
        if (params.authorId) qs.append('author_id', params.authorId);
        if (params.q) qs.append('q', params.q);
        if (params.clientId) qs.append('client_id', params.clientId);
        const s = qs.toString();
        return api.get(`/relazioni${s ? `?${s}` : ''}`);
    },
    get: (id) => api.get(`/relazioni/${id}`),
    create: (data) => api.post('/relazioni', data),
    update: (id, data) => api.put(`/relazioni/${id}`, data),
    delete: (id) => api.delete(`/relazioni/${id}`),
    listDocuments: (id) => api.get(`/relazioni/${id}/documents`),
    uploadDocument: (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/relazioni/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
    viewDocument: (docId) => api.get(`/relazioni/documents/${docId}/download`, { responseType: 'blob' }),
    documentLink: (docId) => api.post(`/relazioni/documents/${docId}/link`),
    deleteDocument: (docId) => api.delete(`/relazioni/documents/${docId}`),
    pdf: (id) => api.get(`/relazioni/${id}/pdf`, { responseType: 'blob' }),
};

// Modelli veicolo
export const modelsAPI = {
    getAll: () => api.get('/models'),
    create: (name) => api.post('/models', { name }),
    delete: (id) => api.delete(`/models/${id}`),
    remap: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/admin/models/remap', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
};

// Reports API
export const reportsAPI = {
    get: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.dateFrom) qs.append('date_from', params.dateFrom);
        if (params.dateTo) qs.append('date_to', params.dateTo);
        if (params.userId) qs.append('user_id', params.userId);
        const s = qs.toString();
        return api.get(`/reports${s ? `?${s}` : ''}`);
    },
    downloadMonthlyExcel: (month, userId) => {
        const qs = new URLSearchParams();
        if (month) qs.append('month', month);
        if (userId) qs.append('user_id', userId);
        const s = qs.toString();
        return api.get(`/reports/monthly.xlsx${s ? `?${s}` : ''}`, { responseType: 'blob' });
    },
    territories: () => api.get('/reports/territories'),
    models: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.year) qs.append('year', params.year);
        if (params.userId) qs.append('user_id', params.userId);
        if (params.country) qs.append('country', params.country);
        const s = qs.toString();
        return api.get(`/reports/models${s ? `?${s}` : ''}`);
    },
    downloadModelsExcel: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.year) qs.append('year', params.year);
        if (params.userId) qs.append('user_id', params.userId);
        if (params.country) qs.append('country', params.country);
        if (params.modello) qs.append('modello', params.modello);
        const s = qs.toString();
        return api.get(`/reports/models.xlsx${s ? `?${s}` : ''}`, { responseType: 'blob' });
    },
};

export default api;
